// Bootstraps the engine + store + repo + restore + catchup.
// Called once on app mount; returns a wired GameStoreInstance + an unsubscribe
// for the persistence middleware. Keeps the React tree from knowing about
// storage details.

import {
  attachPersistence,
  createGameStore,
  runCatchup,
  type GameStoreInstance,
} from '@biz-kid/store';
import { IndexedDBRepo, type Repo } from '@biz-kid/persistence';
import { dayKeyFromDate, restore, type GameState } from '@biz-kid/engine';
import { builderContent } from '@biz-kid/engine/content/builder';

const SAVE_KEY = 'builder:default';

export type BootResult = {
  store: GameStoreInstance;
  unsubscribePersist: () => void;
  catchup: { daysAdvanced: number; daysSkipped: number };
};

export async function bootApp(repo: Repo = new IndexedDBRepo()): Promise<BootResult> {
  const todayKey = dayKeyFromDate(new Date());
  const saved = await repo.getSnapshot(SAVE_KEY);

  let initialState: GameState | undefined;
  if (saved) {
    const r = restore(saved);
    if (r.ok) initialState = r.state;
    // If restore fails (schema mismatch we can't migrate), fall through to a
    // fresh boot rather than blocking the kid. We log so it's debuggable.
    else console.warn('[boot] restore failed, starting fresh:', r.error);
  }

  const store = createGameStore({
    content: builderContent,
    ...(initialState
      ? { initial: initialState }
      : {
          initialArgs: {
            mode: 'builder',
            // Deterministic-ish seed for fresh runs — minute precision is enough.
            rngSeed: Date.now() & 0x7fffffff,
            dayKey: todayKey,
            displayName: 'Kid',
          },
        }),
  });

  const unsubscribePersist = attachPersistence({
    store,
    repo,
    key: SAVE_KEY,
    debounceMs: 750,
  });

  // Roll wall-clock days that have passed since last save (capped per D4).
  const catchup = runCatchup(store, builderContent, todayKey);

  return { store, unsubscribePersist, catchup };
}
